import { IonApp } from '@ionic/react';
import type { AuthenticationConfig } from '@tbitop/authentication';
import { AuthenticationProvider } from '@tbitop/authentication';
import type { AuthorizationConfig } from '@tbitop/authorization';
import { IfAuthorized } from '@tbitop/authorization';
import { ErrorBoundary } from '@tbitop/error-handling';
import { OneplatformApiProvider } from '@tbitop/oneplatform-api';
import { isCapacitor } from '@tbitop/platform';
import type { TelemetryConfig } from '@tbitop/telemetry';
import { TelemetryProvider } from '@tbitop/telemetry';
import { CenteredLoading, DesignSystem } from '@tbitop/ui';
import type { PropsWithChildren } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppUpdateChecker from './app-update-checker';
import { SideMenuProvider } from './mobile-side-menu';
import { NotAuthorizedScreen } from './not-authorized-screen';
import { MobileRoutingProvider } from './routing';

type GlobalProvidersProps = {
  telemetryConfig: TelemetryConfig;
};

const GlobalProviders = ({ children, telemetryConfig }: PropsWithChildren<GlobalProvidersProps>) => (
  <IonApp>
    <Router>
      <MobileRoutingProvider>
        <DesignSystem>
          <TelemetryProvider config={telemetryConfig}>
            <ErrorBoundary>
              {children}
            </ErrorBoundary>
          </TelemetryProvider>
        </DesignSystem>
      </MobileRoutingProvider>
    </Router>
  </IonApp>
);

type MobileAppProps = {
  platformApiUrl: string;
  appVersionApiUrl: string;
  authenticationConfig: AuthenticationConfig;
  authorizationConfig: AuthorizationConfig;
  telemetryConfig: TelemetryConfig;
};

export const MobileApp = ({
  children,
  platformApiUrl,
  authenticationConfig,
  authorizationConfig,
  telemetryConfig,
  appVersionApiUrl,
}: PropsWithChildren<MobileAppProps>) => (
  <GlobalProviders telemetryConfig={telemetryConfig}>
    <AuthenticationProvider config={authenticationConfig}>
      <OneplatformApiProvider apiUrl={platformApiUrl}>
        <SideMenuProvider>
          {isCapacitor() ? <AppUpdateChecker appVersionApiUrl={appVersionApiUrl}>
            <IfAuthorized
              config={authorizationConfig}
              authenticating={<CenteredLoading />}
              notAuthorized={<NotAuthorizedScreen />}>
              {children}
            </IfAuthorized>
          </AppUpdateChecker> : <IfAuthorized
            config={authorizationConfig}
            authenticating={<CenteredLoading />}
            notAuthorized={<NotAuthorizedScreen />}>
            {children}
          </IfAuthorized>}
        </SideMenuProvider>
      </OneplatformApiProvider>
    </AuthenticationProvider>
  </GlobalProviders>
);
